/**
 * UI Constants and Options
 * Constant arrays and options for UI components
 */

import type {
  MetingStatus,
  StroomStelsel,
  TypeBeveiliging,
  TypeAls,
  TypeAansluiting,
  TypeOnderdeel,
  TypeSmeltpatroon,
  TypeAutomaat,
  MetingTussen,
  TypeMateriaal,
  RapportStatus,
  OfferteStatus,
  InspectieType,
  ProductType,
  FreqBasis,
  PrijsType,
  BeoordelingType
} from '../shared/enums'
import { rapportStatusValues } from '../shared/enums'
import { inspectieTypeLabels } from '../shared/inspection-metadata'

// Option interfaces
export interface SelectOption<T = string> {
  label: string
  value: T
}

// Meting status options
export const metingStatusOptions: SelectOption<MetingStatus>[] = [
  { label: 'Voldoet', value: 'voldoet' },
  { label: 'Voldoet niet', value: 'voldoetNiet' },
  { label: 'Neutraal', value: 'neutraal' },
  { label: 'N.v.t.', value: 'nvt' }
]

// Electrical system options
export const stroomStelselOptions: SelectOption<StroomStelsel>[] = [
  { label: 'TN-C-S', value: 'TN-C-S' },
  { label: 'TN-C', value: 'TN-C' },
  { label: 'TN-S', value: 'TN-S' },
  { label: 'TT', value: 'TT' },
  { label: 'IT', value: 'IT' }
]

// Protection type options
export const typeBeveiligingOptions: SelectOption<TypeBeveiliging>[] = [
  { label: 'Smeltpatroon', value: 'Smeltpatroon' },
  { label: 'Automaat', value: 'Automaat' }
]

// RCD type options
export const typeAlsOptions: SelectOption<TypeAls>[] = [
  { label: 'A', value: 'A' },
  { label: 'AC', value: 'AC' },
  { label: 'B', value: 'B' }
]

// Connection type options
export const typeAansluitingOptions: SelectOption<TypeAansluiting>[] = [
  { label: '1P+N', value: '1P+N' },
  { label: '1P', value: '1P' },
  { label: '3P+N', value: '3P+N' },
  { label: '3P', value: '3P' }
]

// Component type options
export const typeOnderdeelOptions: SelectOption<TypeOnderdeel>[] = [
  { label: 'Groep', value: 'Groep' },
  { label: 'Aardlekautomaat', value: 'Aardlekautomaat' },
  { label: 'Aardlekschakelaar', value: 'Aardlekschakelaar' }
]

// Fuse type options
export const typeSmeltpatroonOptions: SelectOption<TypeSmeltpatroon>[] = [
  { label: 'gG', value: 'gG' },
  { label: 'gF', value: 'gF' },
  { label: 'gL', value: 'gL' }
]

// Circuit breaker type options
export const typeAutomaatOptions: SelectOption<TypeAutomaat>[] = [
  { label: 'B', value: 'B' },
  { label: 'C', value: 'C' },
  { label: 'D', value: 'D' }
]

// Measurement between options (1 phase)
export const metingTussen1faseOptions: SelectOption<MetingTussen>[] = [
  { label: 'L-PE', value: 'L-PE' },
  { label: 'L-N', value: 'L-N' },
  { label: 'N-PE', value: 'N-PE' }
]

// Measurement between options (3 phase)
export const metingTussen3faseOptions: SelectOption<MetingTussen>[] = [
  { label: 'L1-N', value: 'L1-N' },
  { label: 'L2-N', value: 'L2-N' },
  { label: 'L3-N', value: 'L3-N' },
  { label: 'L1-PE', value: 'L1-PE' },
  { label: 'L2-PE', value: 'L2-PE' },
  { label: 'L3-PE', value: 'L3-PE' },
  { label: 'L1-L2', value: 'L1-L2' },
  { label: 'L1-L3', value: 'L1-L3' },
  { label: 'L2-L3', value: 'L2-L3' }
]

// Material type options
export const typeMateriaalOptions: SelectOption<TypeMateriaal>[] = [
  { label: 'Verdeler', value: 'verdeler' },
  { label: 'Onderdeel', value: 'onderdeel' },
  { label: 'Omvormer', value: 'omvormer' }
]

// Report status presentation — SINGLE SOURCE OF TRUTH.
//
// Keyed by RapportStatus so it is exhaustive: adding or removing a status is a
// compile error here AND at every consumer derived from it (labels, options,
// dashboard segments, map/legend colours). Always extend this map first.
export interface RapportStatusMeta {
  label: string
  /** CSS custom property name (resolved by each app's theme.css). */
  colorVar: string
  /** Contrast colour for filled chips/segments on top of `colorVar`. */
  textColor: string
}

export const rapportStatusMeta: Record<RapportStatus, RapportStatusMeta> = {
  nieuw: { label: 'Nieuw', colorVar: '--color-nieuw', textColor: '#fff' },
  bezig: { label: 'Bezig', colorVar: '--color-bezig', textColor: '#000' },
  voldoet: { label: 'Voldoet', colorVar: '--color-voldoet', textColor: '#fff' },
  voldoet_niet: { label: 'Voldoet niet', colorVar: '--color-voldoet_niet', textColor: '#fff' },
  hersteld: { label: 'Hersteld', colorVar: '--color-hersteld', textColor: '#fff' },
  verlopen: { label: 'Verlopen', colorVar: '--color-verlopen', textColor: '#fff' },
  ingetrokken: { label: 'Ingetrokken', colorVar: '--color-ingetrokken', textColor: '#fff' }
}

// Derived helpers — change presentation in `rapportStatusMeta` only.
export const rapportStatusLabels = Object.fromEntries(
  rapportStatusValues.map((status) => [status, rapportStatusMeta[status].label])
) as Record<RapportStatus, string>

export const rapportStatusColorVars = Object.fromEntries(
  rapportStatusValues.map((status) => [status, `var(${rapportStatusMeta[status].colorVar})`])
) as Record<RapportStatus, string>

// Report status options (for selects/multiselects), derived from the SOT.
export const rapportStatusOptions: SelectOption<RapportStatus>[] = rapportStatusValues.map((status) => ({
  label: rapportStatusMeta[status].label,
  value: status
}))

// Quote status options
export const offerteStatusOptions: SelectOption<OfferteStatus>[] = [
  { label: 'Nieuw', value: 'nieuw' },
  { label: 'Aangepast', value: 'aangepast' },
  { label: 'Verzonden', value: 'verzonden' },
  { label: 'Bekeken', value: 'bekeken' },
  { label: 'Deels geaccepteerd', value: 'deels_geaccepteerd' },
  { label: 'Geaccepteerd', value: 'alles_geaccepteerd' },
  { label: 'Foute inzending', value: 'foute_inzending' },
  { label: 'Ingepland', value: 'ingepland' },
  { label: 'Afgewezen', value: 'afgewezen' },
  { label: 'Ingetrokken', value: 'ingetrokken' },
  { label: 'Gefactureerd', value: 'gefactureerd' }
]

// Inspection type options
export const inspectieTypeOptions: SelectOption<InspectieType>[] = [
  { label: 'Scope 8 PI', value: 'scope8pi' },
  { label: 'Scope 8 EBI', value: 'scope8ebi' },
  { label: 'Scope 10', value: 'scope10' },
  { label: 'Scope 12 PI', value: 'scope12pi' },
  { label: 'Scope 12 EBI', value: 'scope12ebi' },
  { label: 'NEN 1010', value: 'nen1010' },
  { label: 'NEN 3140', value: 'nen3140' }
]

// Product type options
export const productTypeOptions: SelectOption<ProductType>[] = [
  ...inspectieTypeOptions,
  { label: 'Kilometers', value: 'km' },
  { label: 'Overige', value: 'overige' },
  { label: 'Scope 8', value: 'scope8' },
  { label: 'Scope 12', value: 'scope12' }
]

export const productTypeLabels: Record<ProductType, string> = {
  ...inspectieTypeLabels,
  km: 'Kilometers',
  overige: 'Overige',
  scope8: 'Scope 8',
  scope12: 'Scope 12'
}

// Frequency basis options
export const freqBasisOptions: SelectOption<FreqBasis>[] = [
  { label: 'Verzekering', value: 'Verzekering' },
  { label: 'NTA8220', value: 'NTA8220' },
  { label: 'NEN3140', value: 'NEN3140' },
  { label: 'TD18', value: 'TD18' }
]

// Price type options
export const prijsTypeOptions: SelectOption<PrijsType>[] = [
  { label: 'Voorgesteld', value: 'voorgesteld' },
  { label: 'Aangepast', value: 'aangepast' }
]

// Assessment type options
export const beoordelingOptions: SelectOption<BeoordelingType>[] = [
  { label: 'Opmerking', value: 'opmerking' },
  { label: 'Gering', value: 'gering' },
  { label: 'Serieus', value: 'serieus' },
  { label: 'Ernstig', value: 'ernstig' }
]
